import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import type { HydratedConnectorDragonTile } from "@/api/connectors/index";

import { hydratedConnectorsQueryOptions } from "@/api/connectors/index";
import { isVisibleConnectorDragonTile } from "@/features/Connectors/connectorTileUtils";

/**
 * Visible connectors that have at least one event, keyed by their Dragon Tile
 * id. Shared by the tile creation picker and the Creative Sphere Event filter
 * so both offer the same events for a connector tile.
 */
export function useConnectorsByDragonTileId(
  siteId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    ...hydratedConnectorsQueryOptions(siteId, queryClient),
    enabled: !!siteId && enabled,
  });

  const connectorByDragonTileId = useMemo(
    () =>
      new Map<string, HydratedConnectorDragonTile>(
        (data ?? [])
          // Split so the type predicate narrows: past this filter every entry
          // is guaranteed to own a `dragonTile`.
          .filter(isVisibleConnectorDragonTile)
          .filter((connector) => (connector.installed.events?.length ?? 0) > 0)
          .map((connector) => [connector.dragonTile.id, connector]),
      ),
    [data],
  );

  return { connectorByDragonTileId, isLoading, isError };
}
